import { baseUrl } from './api.js';
import { DomainApiError } from './api-error.js';

/**
 * Krok NAP4 bod A9 — AI pomoc u poptávek a ve schránce: návrh odpovědi,
 * termíny navržené v e-mailu, vážnost zájemce a podobné nabídky.
 *
 * Stejný vzor jako `mailbox-api.ts`: raw `fetch`, protože routy jsou nové a
 * v generovaném `@reos/api-client` schématu ještě nejsou. NIC z tohoto
 * souboru neodesílá e-mail ani nezakládá událost — odeslání drží
 * `replyMailCard`, založení události `createCalendarEvent`.
 */

export interface ReplyDraftDto {
  readonly text: string | null;
  readonly consistencyResult: 'ok' | 'needs_review' | 'skipped';
  readonly needsReviewNumbers: readonly string[];
  /** `null` = koncept je. Jinak důvod, proč návrh nevznikl (nikdy prázdno bez vysvětlení). */
  readonly note: string | null;
}

export interface ProposedEventDto {
  readonly kind: 'viewing_or_meeting';
  readonly date: string;
  readonly hour: number;
  readonly minute: number;
  readonly sourceQuote: string;
  /** Okamžik v ISO — pásmo kanceláře počítá server. */
  readonly startsAt: string;
}

export type QualificationFinancing = 'confirmed' | 'needed' | 'unknown';
export type QualificationTimeline = 'concrete' | 'vague' | 'unknown';
export type QualificationMotivation = 'high' | 'medium' | 'low' | 'unknown';

export interface QualificationSignalsInput {
  readonly financing: QualificationFinancing;
  readonly timeline: QualificationTimeline;
  readonly motivation: QualificationMotivation;
}

export interface QualificationDto {
  readonly total: number;
  readonly level: 'high' | 'medium' | 'low';
  readonly factors: readonly { readonly name: string; readonly points: number; readonly reason: string }[];
}

export interface SimilarListingsDto {
  readonly items: readonly { readonly propertyId: string; readonly explanation: string; readonly distance: number | null }[];
  readonly semanticRanking: boolean;
  readonly note: string | null;
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const problem = (await res.json().catch(() => null)) as { title?: unknown; type?: unknown; detail?: unknown } | null;
    throw new DomainApiError(
      typeof problem?.detail === 'string'
        ? problem.detail
        : typeof problem?.title === 'string'
          ? problem.title
          : `HTTP ${res.status}`,
      res.status,
      typeof problem?.type === 'string' ? problem.type : undefined,
    );
  }
  return (await res.json()) as T;
}

function send(method: string, path: string, body?: unknown): Promise<Response> {
  return fetch(`${baseUrl}${path}`, {
    method,
    credentials: 'include',
    ...(body === undefined ? {} : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }),
  });
}

/** „Navrhnout odpověď" — koncept do editoru. Nic neodesílá. */
export async function suggestReplyDraft(cardId: string): Promise<ReplyDraftDto> {
  return json<ReplyDraftDto>(await send('POST', `/v1/me/mailbox/cards/${encodeURIComponent(cardId)}/reply-draft`));
}

/** Termíny navržené v e-mailu. Návrhy, ne události. */
export async function listProposedEvents(cardId: string): Promise<readonly ProposedEventDto[]> {
  const body = await json<{ items?: readonly ProposedEventDto[] }>(
    await send('GET', `/v1/me/mailbox/cards/${encodeURIComponent(cardId)}/proposed-events`),
  );
  return body.items ?? [];
}

export async function scoreDemandQualification(
  demandId: string,
  signals: QualificationSignalsInput,
): Promise<QualificationDto> {
  return json<QualificationDto>(
    await send('POST', `/v1/crm/demands/${encodeURIComponent(demandId)}/qualification`, signals),
  );
}

export async function listSimilarListings(demandId: string): Promise<SimilarListingsDto> {
  return json<SimilarListingsDto>(
    await send('GET', `/v1/crm/demands/${encodeURIComponent(demandId)}/similar-listings`),
  );
}
